import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, Sparkles, Terminal, Loader2, Send, Download, RefreshCw, ChevronRight,
  AlertOctagon, Search, Wrench, ListChecks, Stethoscope, CheckCircle2, XCircle,
  HelpCircle as HelpIcon, Zap, ClipboardList, Cpu, Play, History as HistoryIcon,
  Share2, Trash2, X, Eye, FileSignature,
} from "lucide-react";

/**
 * Root Cause Finder AI — agent-less sensor.
 *
 * The AI reasons about the described system as if a sensor were probing it,
 * returning: sensor_probe · diagnostic_checks · ranked hypotheses (with
 * investigate + fix commands). The operator ticks off what they actually ran
 * on the target device and clicks "Recompute verdict" — the AI re-ranks
 * hypotheses live based on the confirmed / refuted evidence.
 */
export default function RootCauseAI() {
  const [problem, setProblem] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [session, setSession] = useState(null);

  // ---- RCA History + shareable link state --------------------------------
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sharedReadOnly, setSharedReadOnly] = useState(false); // true when viewing a teammate's session

  const refreshHistory = async () => {
    try {
      setHistoryLoading(true);
      const r = await api.get("/rca/sessions");
      setHistory(r.data?.sessions || []);
    } catch (e) {
      // History is optional — don't block the page if it fails.
    } finally {
      setHistoryLoading(false);
    }
  };

  // On mount: load history + deep-link a shared session if URL has ?s=<sid>&t=<token>
  useEffect(() => {
    refreshHistory();
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("s");
    const token = url.searchParams.get("t");
    if (!sid) return;
    (async () => {
      setBusy(true);
      try {
        const endpoint = token
          ? `/rca/shared/${sid}?t=${encodeURIComponent(token)}`
          : `/rca/sessions/${sid}`;
        const r = await api.get(endpoint);
        const doc = r.data;
        setResult({
          session_id: doc.session_id,
          generated_at: doc.generated_at,
          report: doc.report,
          share_token: doc.share_token,
          title: doc.title,
        });
        setSession(doc.session_id);
        setProblem(doc.last_problem || doc.problem || "");
        setSharedReadOnly(!!token);
        if (token) toast.success("Shared RCA session loaded (view-only)");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load shared session");
      } finally { setBusy(false); }
    })();
  }, []);

  const loadSession = async (sid) => {
    setBusy(true);
    setHistoryOpen(false);
    try {
      const r = await api.get(`/rca/sessions/${sid}`);
      const doc = r.data;
      setResult({
        session_id: doc.session_id,
        generated_at: doc.generated_at,
        report: doc.report,
        share_token: doc.share_token,
        title: doc.title,
      });
      setSession(doc.session_id);
      setProblem(doc.last_problem || doc.problem || "");
      setSharedReadOnly(false);
      // Reflect in URL so refresh keeps you on the same session (no share token here).
      window.history.replaceState({}, "", `/rca?s=${encodeURIComponent(sid)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load session");
    } finally { setBusy(false); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this RCA session from your history?")) return;
    try {
      await api.delete(`/rca/sessions/${sid}`);
      setHistory((h) => h.filter((x) => x.session_id !== sid));
      if (session === sid) {
        // If the deleted session is the currently open one, clear the view.
        reset();
      }
      toast.success("Session deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const startDiagnosis = async () => {
    if (!problem.trim()) return toast.error("Describe the incident first");
    setBusy(true);
    setResult(null);
    setSharedReadOnly(false);
    try {
      const r = await api.post("/rca/diagnose", { problem });
      setResult(r.data);
      setSession(r.data.session_id);
      window.history.replaceState({}, "", `/rca?s=${encodeURIComponent(r.data.session_id)}`);
      refreshHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnosis failed");
    } finally { setBusy(false); }
  };

  const refine = async () => {
    if (!followUp.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/rca/diagnose", {
        problem, follow_up: followUp, session_id: session,
      });
      setResult(r.data);
      setFollowUp("");
      refreshHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refinement failed");
    } finally { setBusy(false); }
  };

  const recompute = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post("/rca/diagnose", {
        problem, session_id: session, operator_findings: payload,
      });
      setResult(r.data);
      toast.success("Verdict recomputed with your findings");
      refreshHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recompute failed");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setResult(null);
    setSession(null);
    setFollowUp("");
    setSharedReadOnly(false);
    window.history.replaceState({}, "", "/rca");
  };

  const downloadReport = () => {
    const r = result?.report; if (!r) return;
    const md = [
      "# ForensiQ · Root Cause Finder AI · Sensor Report",
      `Generated: ${result.generated_at}`,
      "",
      `## Verdict`,
      `**${r.summary_verdict?.level?.toUpperCase()}** — ${r.summary_verdict?.headline}`,
      "",
      `## Problem statement`,
      r.problem_statement || "",
      "",
      `## Sensor probe`,
      "```json",
      JSON.stringify(r.sensor_probe || {}, null, 2),
      "```",
      "",
      `## System profile (inferred)`,
      "```json",
      JSON.stringify(r.system_profile || {}, null, 2),
      "```",
      "",
      `## Aspect-by-aspect health checklist`,
      ...(r.diagnostic_checks || []).flatMap((c, i) => [
        `### ${i + 1}. ${c.aspect}  \`${String(c.likely_status || "unknown").toUpperCase()}\``,
        c.expected ? `Expected: ${c.expected}` : "",
        c.why ? `Why: ${c.why}` : "",
        c.verify_cmd ? "Verify:\n```\n" + c.verify_cmd + "\n```" : "",
        "",
      ]).filter(Boolean),
      "",
      `## Recent changes to check`,
      ...(r.recent_changes_to_check || []).map((c) => `- ${c}`),
      "",
      `## Ranked hypotheses`,
      ...(r.hypotheses || []).flatMap((h, i) => [
        `### ${i + 1}. ${h.cause}  \`${h.likelihood}\`  ${h.confidence_pct != null ? `(${h.confidence_pct}%)` : ""}`,
        `**Why:** ${h.why}`,
        `**Investigate:**`,
        ...(h.investigate_cmds || []).map((c) => "```\n" + c + "\n```"),
        `**Fix:**`,
        ...(h.fix_cmds || []).map((c) => "```\n" + c + "\n```"),
        "",
      ]),
      "",
      `## Next actions`,
      ...(r.next_actions || []).map((a) => `- ${a}`),
      "",
      "---",
      "Built by Ahamed Fasudeen · ForensiQ Root Cause Finder AI",
    ].join("\n");
    const blob = new Blob([md], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ForensiQ-RCA-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.md`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="rca-page">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <Cpu className="w-3.5 h-3.5 text-[#00F0FF]" /> Agent-less sensor · reasons from your description · guided fix runner
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Root Cause Finder AI</h1>
          <p className="text-[#8E9196] mt-1 text-sm max-w-3xl">
            Describe any P1 in plain English (endpoint, agent, application, network — anything).
            The AI behaves like an <span className="text-[#00F0FF]">agent-less sensor / scanner</span> that
            reasons about the described system and returns an aspect-by-aspect health checklist
            (internet ✓ / port 443 blocked / agent corrupted / DNS resolves…) plus ranked
            hypotheses with copy-pasteable investigate + fix commands. Tick off each check
            as you run it — the AI re-ranks the verdict live based on your findings.
          </p>
          {sharedReadOnly && (
            <div className="mt-2 inline-flex items-center gap-1.5 border border-[#B57BFF]/40 bg-[#B57BFF]/[0.08] text-[#B57BFF] px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest"
                 data-testid="rca-shared-banner">
              <Eye className="w-3 h-3" /> Viewing a shared session · read-only
            </div>
          )}
        </div>
        <button
          onClick={() => { setHistoryOpen(true); refreshHistory(); }}
          data-testid="rca-history-open"
          className="shrink-0 inline-flex items-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest"
        >
          <HistoryIcon className="w-3.5 h-3.5" /> History
          {history.length > 0 && (
            <span className="ml-1 text-[#00F0FF]">({history.length})</span>
          )}
        </button>
      </header>

      {!result && !busy && (
        <div className="border border-white/15 bg-[#0a0a0a] rounded-sm p-5 mb-4" data-testid="rca-input">
          <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            Describe the incident (the more detail, the sharper the diagnosis)
          </label>
          <textarea
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            data-testid="rca-problem"
            rows={5}
            placeholder="e.g. CrowdStrike Falcon agent on this Windows 11 laptop stopped reporting to the console yesterday. Users say Chrome and Outlook work fine — internet is up. No errors in Event Viewer. Or: SentinelOne agent shows offline in mgmt console for 40 endpoints in the Chennai office since Wednesday."
            className="w-full mt-2 bg-transparent border border-white/15 rounded-sm p-3 text-sm font-mono outline-none focus:border-[#00F0FF] resize-none"
          />
          <div className="mt-3 flex items-center justify-between">
            <div className="text-[10px] font-mono text-[#52525B]">
              Include: OS, service, symptom, time it started, recent changes if known.
            </div>
            <button
              onClick={startDiagnosis}
              disabled={!problem.trim()}
              data-testid="rca-start"
              className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black disabled:opacity-40 px-4 py-2 rounded-sm font-mono uppercase text-xs tracking-widest"
            >
              <Sparkles className="w-3.5 h-3.5" /> Start scan
            </button>
          </div>
        </div>
      )}

      {busy && <ScanAnimation />}

      {result && !busy && (
        <Report
          data={result}
          onReset={reset}
          onDownload={downloadReport}
          followUp={followUp}
          setFollowUp={setFollowUp}
          onRefine={refine}
          onRecompute={recompute}
          readOnly={sharedReadOnly}
        />
      )}

      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        items={history}
        loading={historyLoading}
        onLoad={loadSession}
        onDelete={deleteSession}
        activeSession={session}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- */

function ScanAnimation() {
  const canvasRef = useRef(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.clientWidth * dpr;
    const H = canvas.clientHeight * dpr;
    canvas.width = W; canvas.height = H;
    const fontSize = 14 * dpr;
    const cols = Math.floor(W / fontSize);
    const drops = new Array(cols).fill(0).map(() => Math.random() * -50);
    const chars = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモ0123456789ABCDEF/><=[]{}";
    let raf;
    const paint = () => {
      ctx.fillStyle = "rgba(6, 6, 6, 0.09)";
      ctx.fillRect(0, 0, W, H);
      ctx.font = `${fontSize}px monospace`;
      for (let i = 0; i < cols; i++) {
        const ch = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillStyle = drops[i] % 20 < 1 ? "#B57BFF" : "#00F0FF";
        ctx.fillText(ch, i * fontSize, drops[i] * fontSize);
        drops[i] = drops[i] * fontSize > H && Math.random() > 0.975 ? 0 : drops[i] + 1;
      }
      raf = requestAnimationFrame(paint);
    };
    paint();
    return () => cancelAnimationFrame(raf);
  }, []);

  const phases = [
    "Fingerprinting target OS…",
    "Enumerating sensor probes…",
    "Testing network reachability (simulated)…",
    "Resolving control-plane hosts (simulated)…",
    "Probing outbound ports (simulated)…",
    "Cross-referencing symptom against knowledge base…",
    "Ranking root-cause hypotheses…",
    "Composing investigation commands…",
    "Composing remediation commands…",
    "Finalizing sensor report…",
  ];
  useEffect(() => {
    const t = setInterval(() => setTick((n) => (n + 1) % phases.length), 1400);
    return () => clearInterval(t);
  }, [phases.length]);

  return (
    <div className="relative border border-[#00F0FF]/30 rounded-sm overflow-hidden bg-[#050505] h-[480px]" data-testid="rca-scanning">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-transparent to-[#050505]/90" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center gap-6 p-8">
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-2 border-[#00F0FF]/40 animate-ping absolute inset-0" />
          <div className="w-24 h-24 rounded-full border-2 border-[#00F0FF] flex items-center justify-center bg-[#050505]/80">
            <Radar className="w-10 h-10 text-[#00F0FF] animate-spin" style={{ animationDuration: "3s" }} />
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] mb-2">Agent-less sensor · scanning</div>
          <div className="font-display font-black text-3xl mb-1">Analyzing incident…</div>
          <div className="text-[#8E9196] font-mono text-sm h-6">
            <Loader2 className="w-3 h-3 inline mr-2 animate-spin" />
            {phases[tick]}
          </div>
        </div>
        <div className="w-full max-w-md h-1 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#00F0FF] via-[#B57BFF] to-[#00F0FF] animate-pulse" style={{ width: "80%" }} />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */

function Report({ data, onReset, onDownload, followUp, setFollowUp, onRefine, onRecompute, readOnly }) {
  const r = data?.report || {};
  const verdictColor = { red: "#FF3B30", amber: "#FFCC00", green: "#34C759" }[r.summary_verdict?.level] || "#8E9196";
  const verdictLabel = { red: "Critical", amber: "Attention needed", green: "Low risk" }[r.summary_verdict?.level] || "•";

  // ---- Guided Fix Runner state ------------------------------------
  // checkFindings[idx] = { result, notes, output }
  const [checkFindings, setCheckFindings] = useState({});
  // cmdFindings[`h${hi}-${kind}-${ci}`] = { result, notes, output }
  const [cmdFindings, setCmdFindings] = useState({});

  // Reset ticks whenever a NEW report arrives (generated_at is the reliable key).
  useEffect(() => {
    setCheckFindings({});
    setCmdFindings({});
  }, [data?.generated_at]);

  const tickedChecks = Object.entries(checkFindings).filter(([, v]) => v?.result);
  const tickedCmds = Object.entries(cmdFindings).filter(([, v]) => v?.result);
  const totalTicked = tickedChecks.length + tickedCmds.length;

  const shareUrl = data?.share_token && data?.session_id
    ? `${window.location.origin}/rca?s=${encodeURIComponent(data.session_id)}&t=${encodeURIComponent(data.share_token)}`
    : null;

  const doShare = async () => {
    if (!shareUrl) return toast.error("Share link not available for this session");
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Shareable link copied — send it to a teammate in your tenant");
    } catch {
      // Fallback: show the URL in a prompt
      window.prompt("Copy this shareable link:", shareUrl);
    }
  };

  const [runbookBusy, setRunbookBusy] = useState(false);
  const downloadRunbook = async () => {
    if (!data?.session_id) return;
    setRunbookBusy(true);
    try {
      // Use shared endpoint when viewing a teammate's session; owner endpoint otherwise.
      const url = readOnly && data.share_token
        ? `/rca/shared/${data.session_id}/runbook.pdf?t=${encodeURIComponent(data.share_token)}`
        : `/rca/sessions/${data.session_id}/runbook.pdf`;
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `ForensiQ-Runbook-${data.session_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Signed runbook downloaded — attach it to your incident ticket");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Runbook download failed");
    } finally { setRunbookBusy(false); }
  };

  const buildFindingsPayload = () => {
    const checks = tickedChecks.map(([idxStr, v]) => {
      const idx = parseInt(idxStr, 10);
      const c = (r.diagnostic_checks || [])[idx] || {};
      return { aspect: c.aspect, result: v.result, notes: v.notes || "", output: v.output || "" };
    });
    const commands = tickedCmds.map(([key, v]) => {
      const [hpart, kind, cpart] = key.split("-");
      const hi = parseInt(hpart.replace("h", ""), 10);
      const ci = parseInt(cpart, 10);
      const h = (r.hypotheses || [])[hi] || {};
      const cmd = (kind === "inv" ? h.investigate_cmds : h.fix_cmds)?.[ci];
      return {
        hypothesis: h.cause,
        kind: kind === "inv" ? "investigate" : "fix",
        cmd,
        result: v.result,
        notes: v.notes || "",
        output: v.output || "",
      };
    });
    return { checks, commands };
  };

  const doRecompute = () => {
    if (readOnly) return toast.error("Read-only view — start your own diagnosis to recompute");
    if (totalTicked === 0) return toast.error("Tick at least one item first");
    onRecompute(buildFindingsPayload());
  };

  const setCheck = (idx, patch) =>
    setCheckFindings((s) => ({ ...s, [idx]: { ...(s[idx] || {}), ...patch } }));
  const setCmd = (key, patch) =>
    setCmdFindings((s) => ({ ...s, [key]: { ...(s[key] || {}), ...patch } }));

  return (
    <div className="space-y-4" data-testid="rca-report">
      {/* Verdict */}
      <div className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a]" style={{ borderLeft: `4px solid ${verdictColor}` }}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: verdictColor }}>
              Verdict · {verdictLabel}
            </div>
            <div className="font-display text-2xl leading-tight mb-2" data-testid="rca-verdict-headline">{r.summary_verdict?.headline}</div>
            <div className="text-sm text-[#8E9196]">{r.problem_statement}</div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!readOnly && (
              <button
                onClick={doRecompute}
                disabled={totalTicked === 0}
                data-testid="rca-recompute"
                title={totalTicked === 0 ? "Tick at least one check/command below, then click to re-rank" : `Re-rank verdict using ${totalTicked} ticked finding(s)`}
                className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#00F0FF] px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest"
              >
                <Zap className="w-3.5 h-3.5" /> Recompute
                {totalTicked > 0 && <span className="ml-1 opacity-90">({totalTicked})</span>}
              </button>
            )}
            <div className="flex gap-2 flex-wrap">
              {data?.session_id && (
                <button
                  onClick={downloadRunbook}
                  disabled={runbookBusy}
                  data-testid="rca-runbook"
                  title="Download an HMAC-signed PDF runbook you can attach to the incident ticket"
                  className="inline-flex items-center gap-1.5 border border-[#00F0FF]/60 hover:border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
                  {runbookBusy
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <FileSignature className="w-3.5 h-3.5" />}
                  Runbook
                </button>
              )}
              {shareUrl && (
                <button onClick={doShare} data-testid="rca-share"
                  title="Copy a link teammates in your tenant can open"
                  className="inline-flex items-center gap-1.5 border border-[#B57BFF]/60 hover:border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                  <Share2 className="w-3.5 h-3.5" /> Share
                </button>
              )}
              <button onClick={onDownload} data-testid="rca-download" className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF] text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                <Download className="w-3.5 h-3.5" /> MD
              </button>
              <button onClick={onReset} data-testid="rca-reset" className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white text-[#8E9196] px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">
                <RefreshCw className="w-3.5 h-3.5" /> New
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sensor probe strip */}
      <SensorProbe probe={r.sensor_probe} hypotheses={r.hypotheses} />

      <div className="grid md:grid-cols-3 gap-4">
        {/* System profile */}
        <div className="border border-white/10 rounded-sm p-4 bg-[#0a0a0a] md:col-span-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-2">System profile <span className="text-[#52525B]">(inferred)</span></div>
          <dl className="space-y-1 text-xs font-mono">
            {Object.entries(r.system_profile || {}).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[#8E9196]">{k}</dt>
                <dd className="text-white ml-2 break-words">
                  {Array.isArray(v) ? v.join(", ") : String(v || "-")}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Recent changes */}
        <div className="border border-white/10 rounded-sm p-4 bg-[#0a0a0a] md:col-span-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFCC00] mb-2">Recent changes to check</div>
          <ul className="space-y-1 text-sm">
            {(r.recent_changes_to_check || []).map((c, i) => (
              <li key={i} className="flex gap-2">
                <ChevronRight className="w-3 h-3 text-[#FFCC00] mt-1 shrink-0" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Diagnostic checklist — TICKABLE */}
      {Array.isArray(r.diagnostic_checks) && r.diagnostic_checks.length > 0 && (
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a]" data-testid="rca-diagnostic-checks">
          <div className="px-4 pt-4 pb-2 text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] flex items-center gap-2">
            <Stethoscope className="w-3.5 h-3.5" /> Aspect-by-aspect health checklist
            <span className="text-[#52525B]">· tick what you actually verified on the target device</span>
          </div>
          <div className="divide-y divide-white/5">
            {r.diagnostic_checks.map((c, i) => (
              <DiagnosticRow
                key={i}
                check={c}
                idx={i}
                finding={checkFindings[i]}
                onFinding={(patch) => setCheck(i, patch)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hypotheses — each cmd is TICKABLE */}
      <div className="space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] flex items-center gap-2">
          <AlertOctagon className="w-3.5 h-3.5" /> Ranked root cause hypotheses
          <span className="text-[#52525B]">· tick each command as you run it</span>
        </div>
        {(r.hypotheses || []).map((h, i) => (
          <div key={i} className="border border-white/10 rounded-sm bg-[#0a0a0a]" data-testid={`rca-hypothesis-${i}`}>
            <div className="p-4 border-b border-white/5">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display font-black text-[#B57BFF]">#{i + 1}</span>
                <span className="font-display text-lg flex-1">{h.cause}</span>
                {h.confidence_pct != null && (
                  <span className="font-mono text-[10px] tracking-widest text-[#8E9196]">{h.confidence_pct}%</span>
                )}
                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm"
                  style={{
                    color: h.likelihood === "high" ? "#FF3B30" : h.likelihood === "medium" ? "#FFCC00" : "#34C759",
                    borderColor: (h.likelihood === "high" ? "#FF3B30" : h.likelihood === "medium" ? "#FFCC00" : "#34C759") + "60",
                  }}>
                  {h.likelihood}
                </span>
              </div>
              <div className="text-sm text-[#8E9196]">{h.why}</div>
              {h.confidence_pct != null && (
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full transition-all duration-500"
                    style={{
                      width: `${Math.max(3, Math.min(100, h.confidence_pct))}%`,
                      background: `linear-gradient(90deg, ${h.likelihood === "high" ? "#FF3B30" : h.likelihood === "medium" ? "#FFCC00" : "#34C759"}, #B57BFF)`,
                    }}
                  />
                </div>
              )}
            </div>
            <div className="p-4 grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1.5 flex items-center gap-1">
                  <Search className="w-3 h-3" /> Investigate
                </div>
                {(h.investigate_cmds || []).map((c, j) => (
                  <CommandRow
                    key={`inv-${j}`}
                    testid={`rca-h${i}-inv-${j}`}
                    cmd={c}
                    finding={cmdFindings[`h${i}-inv-${j}`]}
                    onFinding={(patch) => setCmd(`h${i}-inv-${j}`, patch)}
                    tone="investigate"
                  />
                ))}
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#34C759] mb-1.5 flex items-center gap-1">
                  <Wrench className="w-3 h-3" /> Fix
                </div>
                {(h.fix_cmds || []).map((c, j) => (
                  <CommandRow
                    key={`fix-${j}`}
                    testid={`rca-h${i}-fix-${j}`}
                    cmd={c}
                    finding={cmdFindings[`h${i}-fix-${j}`]}
                    onFinding={(patch) => setCmd(`h${i}-fix-${j}`, patch)}
                    tone="fix"
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sticky Recompute footer — only visible while operator has ticks pending */}
      {totalTicked > 0 && (
        <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none">
          <div className="pointer-events-auto flex items-center gap-3 border border-[#00F0FF] bg-[#0a0a0a]/95 backdrop-blur px-4 py-2 rounded-sm shadow-[0_0_30px_rgba(0,240,255,0.25)]"
               data-testid="rca-recompute-strip">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">
              <ClipboardList className="w-3.5 h-3.5 inline mr-1.5" />
              {totalTicked} finding{totalTicked === 1 ? "" : "s"} pending
            </div>
            <button onClick={doRecompute}
              data-testid="rca-recompute-sticky"
              className="inline-flex items-center gap-1.5 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-1.5 rounded-sm font-mono uppercase text-xs tracking-widest">
              <Zap className="w-3.5 h-3.5" /> Recompute verdict
            </button>
          </div>
        </div>
      )}

      {/* Next actions */}
      <div className="border border-white/10 rounded-sm p-4 bg-[#0a0a0a]">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#34C759] mb-2 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Next actions</div>
        <ol className="space-y-1 text-sm">
          {(r.next_actions || []).map((a, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[#34C759] font-mono">{String(i + 1).padStart(2, "0")}.</span>
              <span>{a}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Follow-up chat */}
      <div className="border border-[#B57BFF]/20 bg-[#B57BFF]/[0.03] rounded-sm p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] mb-2 flex items-center gap-1">
          <Terminal className="w-3.5 h-3.5" /> Add free-form context or paste extra output
        </div>
        <div className="flex gap-2">
          <input
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), onRefine())}
            placeholder="e.g. 'Windows Firewall rule was changed by Group Policy on Wednesday' — the AI will use this to refine"
            data-testid="rca-followup"
            className="flex-1 bg-transparent border border-white/15 rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#B57BFF]"
          />
          <button onClick={onRefine} disabled={!followUp.trim()} data-testid="rca-refine"
            className="inline-flex items-center gap-1.5 border border-[#B57BFF] text-[#B57BFF] hover:bg-[#B57BFF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-40">
            <Send className="w-3.5 h-3.5" /> Refine
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */

function SensorProbe({ probe, hypotheses }) {
  // Derive scanned aspects: prefer probe.scanned_aspects, else fall back to a
  // slug of the diagnostic_checks aspects (handled by parent — we get array).
  const aspects = Array.isArray(probe?.scanned_aspects) ? probe.scanned_aspects : [];
  const confidence = String(probe?.confidence || "medium").toLowerCase();
  const confColor = { high: "#34C759", medium: "#FFCC00", low: "#FF3B30" }[confidence] || "#8E9196";
  const top = (hypotheses || [])[0];

  return (
    <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/[0.03] rounded-sm p-4" data-testid="rca-sensor-probe">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1 flex items-center gap-1.5">
            <Cpu className="w-3.5 h-3.5" /> Sensor probe
            <span className="text-[#52525B]">· agent-less</span>
          </div>
          <div className="text-sm text-white mb-1.5">
            {probe?.reasoning || "Reasoning about the described system as an agent-less scanner."}
          </div>
          {aspects.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {aspects.map((a, i) => (
                <span key={i} className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#00F0FF]/40 text-[#00F0FF] rounded-sm">
                  {String(a).replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Scan confidence</div>
          <div className="font-display text-2xl font-black" style={{ color: confColor }}>
            {confidence.toUpperCase()}
          </div>
          {top?.confidence_pct != null && (
            <div className="text-[10px] font-mono text-[#52525B] mt-1">
              Top hypothesis: <span className="text-white">{top.confidence_pct}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */

function TickPills({ finding, onFinding, options, testid }) {
  const cur = finding?.result || null;
  return (
    <div className="flex gap-1" data-testid={testid}>
      {options.map((o) => {
        const active = cur === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onFinding({ result: active ? null : o.value })}
            data-testid={`${testid}-${o.value}`}
            title={o.title}
            className={`inline-flex items-center gap-1 border rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${active ? "text-black" : ""}`}
            style={{
              color: active ? "#000" : o.color,
              backgroundColor: active ? o.color : "transparent",
              borderColor: o.color + (active ? "" : "60"),
            }}
          >
            <o.Icon className="w-3 h-3" /> {o.label}
          </button>
        );
      })}
    </div>
  );
}

const CHECK_OPTIONS = [
  { value: "confirmed_ok",  label: "OK",      title: "Confirmed OK on the target",  color: "#34C759", Icon: CheckCircle2 },
  { value: "confirmed_bad", label: "BAD",     title: "Confirmed FAILED on the target", color: "#FF3B30", Icon: XCircle },
  { value: "skipped",       label: "Skip",    title: "Couldn't verify / skipped",    color: "#8E9196", Icon: HelpIcon },
];

const CMD_OPTIONS = [
  { value: "ok",      label: "OK",   title: "Ran, result was expected/good", color: "#34C759", Icon: CheckCircle2 },
  { value: "fail",    label: "FAIL", title: "Ran, output shows failure",     color: "#FF3B30", Icon: XCircle },
  { value: "unknown", label: "?",    title: "Ran, output unclear or skipped", color: "#8E9196", Icon: HelpIcon },
];

function StatusPill({ status }) {
  const s = String(status || "unknown").toLowerCase();
  const map = {
    ok:       { color: "#34C759", label: "OK",       Icon: CheckCircle2 },
    blocked:  { color: "#FF3B30", label: "BLOCKED",  Icon: XCircle },
    degraded: { color: "#FFCC00", label: "DEGRADED", Icon: AlertOctagon },
    unknown:  { color: "#8E9196", label: "UNKNOWN",  Icon: HelpIcon },
  };
  const pill = map[s] || { color: "#8E9196", label: s.toUpperCase(), Icon: HelpIcon };
  const Icon = pill.Icon;
  return (
    <span className="inline-flex items-center gap-1 border rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest"
          style={{ color: pill.color, borderColor: pill.color + "60" }}>
      <Icon className="w-3 h-3" /> {pill.label}
    </span>
  );
}

function DiagnosticRow({ check, idx, finding, onFinding }) {
  const [expanded, setExpanded] = useState(false);
  const ticked = !!finding?.result;

  // Auto-expand once ticked so operator sees the notes/output boxes.
  useEffect(() => { if (ticked) setExpanded(true); }, [ticked]);

  return (
    <div className="px-4 py-3" data-testid={`rca-check-${idx}`}>
      <div className="grid md:grid-cols-[220px_120px_1fr_auto] gap-3 items-start">
        <div className="text-sm text-white flex items-start gap-2">
          <span className="font-mono text-[10px] text-[#52525B] mt-0.5">{String(idx + 1).padStart(2, "0")}</span>
          <span className="font-semibold">{check.aspect}</span>
        </div>
        <div>
          <StatusPill status={check.likely_status} />
          {check.expected && (
            <div className="text-[10px] font-mono text-[#52525B] mt-1">expects: {check.expected}</div>
          )}
        </div>
        <div className="text-xs">
          {check.why && <div className="text-[#8E9196] mb-1">{check.why}</div>}
          {check.verify_cmd && (
            <pre className="text-[11px] bg-black/40 border border-white/5 rounded-sm p-2 overflow-x-auto text-white/85 font-mono">{check.verify_cmd}</pre>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <TickPills
            finding={finding}
            onFinding={onFinding}
            options={CHECK_OPTIONS}
            testid={`rca-check-${idx}-tick`}
          />
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[9px] font-mono uppercase tracking-widest text-[#52525B] hover:text-white"
          >
            {expanded ? "hide notes" : "add notes / output"}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-2 grid md:grid-cols-2 gap-2" data-testid={`rca-check-${idx}-details`}>
          <textarea
            value={finding?.notes || ""}
            onChange={(e) => onFinding({ notes: e.target.value })}
            placeholder="Optional notes for the AI (e.g. 'this endpoint sits behind a proxy at 10.0.0.5')"
            rows={2}
            data-testid={`rca-check-${idx}-notes`}
            className="w-full bg-transparent border border-white/10 rounded-sm p-2 text-[11px] font-mono outline-none focus:border-[#00F0FF] resize-none"
          />
          <textarea
            value={finding?.output || ""}
            onChange={(e) => onFinding({ output: e.target.value })}
            placeholder="Optional: paste raw command output so the AI can read it"
            rows={2}
            data-testid={`rca-check-${idx}-output`}
            className="w-full bg-black/40 border border-white/10 rounded-sm p-2 text-[11px] font-mono outline-none focus:border-[#00F0FF] resize-none text-white/85"
          />
        </div>
      )}
    </div>
  );
}

function CommandRow({ testid, cmd, finding, onFinding, tone }) {
  const [expanded, setExpanded] = useState(false);
  const ticked = !!finding?.result;
  useEffect(() => { if (ticked) setExpanded(true); }, [ticked]);
  const toneColor = tone === "fix" ? "#34C759" : "#00F0FF";
  const copy = async () => {
    try { await navigator.clipboard.writeText(cmd); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="mb-2" data-testid={testid}>
      <div className="flex items-start gap-2">
        <pre className="flex-1 text-[11px] bg-black/40 border border-white/5 rounded-sm p-2 overflow-x-auto text-white/85 font-mono">{cmd}</pre>
        <div className="flex flex-col gap-1 shrink-0">
          <TickPills
            finding={finding}
            onFinding={onFinding}
            options={CMD_OPTIONS}
            testid={`${testid}-tick`}
          />
          <div className="flex gap-1 justify-end">
            <button type="button" onClick={copy}
              className="text-[9px] font-mono uppercase tracking-widest text-[#52525B] hover:text-white"
              data-testid={`${testid}-copy`}>
              copy
            </button>
            <button type="button" onClick={() => setExpanded((v) => !v)}
              className="text-[9px] font-mono uppercase tracking-widest text-[#52525B] hover:text-white">
              {expanded ? "hide" : "notes"}
            </button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="mt-1.5 grid md:grid-cols-2 gap-2">
          <textarea
            value={finding?.notes || ""}
            onChange={(e) => onFinding({ notes: e.target.value })}
            placeholder="Notes (what did you observe?)"
            rows={2}
            data-testid={`${testid}-notes`}
            className="w-full bg-transparent border border-white/10 rounded-sm p-2 text-[11px] font-mono outline-none resize-none"
            style={{ borderColor: toneColor + "30" }}
          />
          <textarea
            value={finding?.output || ""}
            onChange={(e) => onFinding({ output: e.target.value })}
            placeholder="Paste raw command output"
            rows={2}
            data-testid={`${testid}-output`}
            className="w-full bg-black/40 border border-white/10 rounded-sm p-2 text-[11px] font-mono outline-none resize-none text-white/85"
            style={{ borderColor: toneColor + "30" }}
          />
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */

function HistoryDrawer({ open, onClose, items, loading, onLoad, onDelete, activeSession }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" data-testid="rca-history-drawer">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"
           onClick={onClose} data-testid="rca-history-backdrop" />
      {/* Panel */}
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-[#0a0a0a] border-l border-white/10 overflow-y-auto"
             onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-[#0a0a0a] border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HistoryIcon className="w-4 h-4 text-[#00F0FF]" />
            <div>
              <div className="font-display font-black text-lg leading-tight">RCA History</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B]">
                last {items.length}/20 · your sessions
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="rca-history-close"
            className="text-[#8E9196] hover:text-white p-1 rounded-sm">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-3 space-y-2">
          {loading && (
            <div className="flex items-center gap-2 text-xs font-mono text-[#8E9196] p-4">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center py-8 px-4">
              <HistoryIcon className="w-8 h-8 text-[#52525B] mx-auto mb-2" />
              <div className="text-sm text-[#8E9196]">No RCA sessions yet.</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mt-1">
                Start a diagnosis — it&apos;ll show up here automatically.
              </div>
            </div>
          )}
          {items.map((s) => {
            const isActive = s.session_id === activeSession;
            const verdictColor = { red: "#FF3B30", amber: "#FFCC00", green: "#34C759" }[s.verdict_level] || "#8E9196";
            const when = s.updated_at ? new Date(s.updated_at).toLocaleString() : "";
            return (
              <div key={s.session_id}
                data-testid={`rca-history-item-${s.session_id}`}
                className={`border rounded-sm p-3 group transition-colors ${
                  isActive ? "border-[#00F0FF] bg-[#00F0FF]/[0.04]" : "border-white/10 hover:border-white/25"
                }`}>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: verdictColor }} />
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => onLoad(s.session_id)}
                      data-testid={`rca-history-load-${s.session_id}`}
                      className="text-left w-full"
                    >
                      <div className="text-sm text-white line-clamp-2 group-hover:text-[#00F0FF]">
                        {s.title || "Untitled diagnosis"}
                      </div>
                      {s.top_hypothesis && (
                        <div className="text-[10px] font-mono text-[#8E9196] line-clamp-1 mt-0.5">
                          → {s.top_hypothesis}
                        </div>
                      )}
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mt-1">
                        {when}
                        {isActive && <span className="ml-2 text-[#00F0FF]">· open</span>}
                      </div>
                    </button>
                  </div>
                  <button
                    onClick={() => onDelete(s.session_id)}
                    data-testid={`rca-history-delete-${s.session_id}`}
                    title="Delete from history"
                    className="text-[#52525B] hover:text-[#FF3B30] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}

/* Reserved for future — keep imports happy */
export { StatusPill, DiagnosticRow, CommandRow, SensorProbe, HistoryDrawer, Play };
